/**
 * Functions for building URLs for hyperlinks to entities (Controls, Issues, Third-Party Services, etc.) within SummIT (i.e., React routes).
 * This has nothing to do with SummIT back-end API URLs. Those are defined within the src/Api/ folder.
 */
import { generatePath } from 'react-router';

import { ACTION_DETAILS, ISSUES_EXCEPTIONS_CONTROLS_MODULE, TPRM_ADD_FOLDER_VERSION, TPRM_ADD_NEW_FOLDER, TPRM_ASSESSMENT, TPRM_FINAL_REVIEW, TPRM_FOLDERS_LISTING, TPRM_FOLDER_DETAILS, TPRM_INHERENT_RISK_QUESTIONNAIRE, TPRM_ISSUES_EXCEPTIONS, TPRM_MANAGE_FOLDER, TPRM_SERVICE_DASHBOARD, TPRM_THIRD_PARTIES_AND_SERVICES_LISTING, UCRM_ADD_FOLDER_VERSION, UCRM_ADD_NEW_FOLDER, UCRM_ASSESSMENT, UCRM_BUSINESSES_AND_USE_CASES_LISTING, UCRM_FINAL_REVIEW, UCRM_FOLDERS_LISTING, UCRM_FOLDER_DETAILS, UCRM_INHERENT_RISK_QUESTIONNAIRE, UCRM_ISSUES_EXCEPTIONS, UCRM_MANAGE_FOLDER, UCRM_USE_CASE_DASHBOARD } from 'Config/PathTemplates';
import { CONFIGURATION, CONTROLS, CREATE, EXCEPTIONS, FRAMEWORKS, GROUPS, ISSUES, OPERATIONAL_CONTROLS, TPRM, UCRM } from 'Config/Paths';
import { ExceptionBreadcrumbSource, ManageExceptionOrigin } from 'Models/Exceptions';
import { FolderBreadcrumbSource, ManageFolderOrigin } from 'Models/Folders';
import { IssueBreadcrumbSource, ManageIssueOrigin } from 'Models/Issues';
import { NotificationType, UserNotification } from 'Models/Notification';
import { DueDiligenceModule, ProductModule } from 'Models/ProductModule';

/**
 * A single segment of a breadcrumb trail. A segment with a `link` is rendered as a `BreadcrumbLink`; otherwise it is rendered as plain `BreadcrumbText`.
 * Used to build breadcrumbs for the Manage Issue and Manage Exception pages, which can be navigated to from several different places in the app.
 */
export interface BreadcrumbSegment {
    label: string;
    link?: string;
}

/** Search param keys used to carry `ManageIssueOrigin` context (see `getCreateIssueUrl` / `parseManageIssueOrigin`). */
export const SEARCH_PARAM_ISSUE_ORIGIN_SOURCE = 'source';
export const SEARCH_PARAM_ISSUE_ORIGIN_ORGANIZATION_ID = 'originOrganizationId';
export const SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_ID = 'originAssessmentSubjectId';
export const SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_NAME = 'originAssessmentSubjectName';
export const SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_ID = 'originAssessmentId';
export const SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_NAME = 'originAssessmentName';
export const SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_IDENTIFIER = 'originControlIdentifier';
export const SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_GROUP_NAME = 'originControlGroupName';
export const SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_NAME = 'originControlName';
export const SEARCH_PARAM_ISSUE_ORIGIN_MODULE = 'originModule';

/** Search param keys used to carry `ManageExceptionOrigin` context (see `getCreateExceptionUrl` / `parseManageExceptionOrigin`). */
export const SEARCH_PARAM_EXCEPTION_ORIGIN_SOURCE = 'source';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_ORGANIZATION_ID = 'originOrganizationId';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_ID = 'originAssessmentSubjectId';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_NAME = 'originAssessmentSubjectName';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_ID = 'originAssessmentId';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_NAME = 'originAssessmentName';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_IDENTIFIER = 'originControlIdentifier';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_GROUP_NAME = 'originControlGroupName';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_NAME = 'originControlName';
export const SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE = 'originModule';

/** Search param keys used to carry `ManageFolderOrigin` context (see `getCreateFolderUrl` / `parseManageFolderOrigin`). */
export const SEARCH_PARAM_FOLDER_ORIGIN_SOURCE = 'source';
export const SEARCH_PARAM_FOLDER_ORIGIN_ORGANIZATION_ID = 'originOrganizationId';
export const SEARCH_PARAM_FOLDER_ORIGIN_ASSESSMENT_SUBJECT_ID = 'originAssessmentSubjectId';
export const SEARCH_PARAM_FOLDER_ORIGIN_ASSESSMENT_SUBJECT_NAME = 'originAssessmentSubjectName';
export const SEARCH_PARAM_FOLDER_ORIGIN_MODULE = 'originModule';

/** Search param key identifying the organization that the Create Folder page should preselect. Also read by the folders listing, which uses it to preselect its organization filter. */
export const SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID = 'organizationId';

/** Search param key identifying the organization by which the issues and exceptions listings on the Issues & Exceptions page are initially filtered. */
export const SEARCH_PARAM_ISSUES_EXCEPTIONS_ORGANIZATION_ID = 'organizationId';

export const getFrameworkGroupControlURLUnparsableIdentifier = 'Unrecognized identifier.'; // Exported so it can be imported in tests.

/** Returns the URL path prefix (e.g. `/tprm`, `/ucrm`, or none for Controls) for a given Issues/Exceptions module. */
export const pathPrefixForIssuesExceptionsModule = (module: ProductModule): string => {
    switch (module) {
        case ProductModule.TPRM:
            return `/${TPRM}`;
        case ProductModule.UCRM:
            return `/${UCRM}`;
        case ProductModule.CONTROLS:
            return '';
    }
};

/** Returns the path to the standalone Issues & Exceptions listing page for a given module. */
const issuesExceptionsListingPathForModule = (module: ProductModule): string => {
    switch (module) {
        case ProductModule.CONTROLS:
            return generatePath(ISSUES_EXCEPTIONS_CONTROLS_MODULE, {});
        case ProductModule.TPRM:
            return generatePath(TPRM_ISSUES_EXCEPTIONS, {});
        case ProductModule.UCRM:
            return generatePath(UCRM_ISSUES_EXCEPTIONS, {});
    }
};

/** Returns the path to the module's organizations and assessment subjects listing page: Third Parties and Services (TPRM), or Businesses and Use Cases (UCRM). */
const organizationsAndAssessmentSubjectsListingPathForModule = (module: DueDiligenceModule): string => (module === ProductModule.UCRM ? UCRM_BUSINESSES_AND_USE_CASES_LISTING : TPRM_THIRD_PARTIES_AND_SERVICES_LISTING);

/**
 *
 * @param identifier The canonical identifier for a Framework, Group, or Control. For example, "CIS CSC (v8.0)", "CIS CSC (v8.0)#1", or "CIS CSC (v8.0)#1#1.1"
 * @returns a URL (string) based on the provided identifier.
 */
export const getFrameworkGroupControlURL = (identifier: string): string => {
    if (identifier.includes('#')) {
        const parts = identifier.split('#');
        if (parts.length === 2) {
            // The given identifier is a Control Group.
            return `/${OPERATIONAL_CONTROLS}/${FRAMEWORKS}/${parts[0]}/${GROUPS}/${parts[1]}`;
        } else if (parts.length === 3) {
            // The given identifier is a Control.
            return `/${OPERATIONAL_CONTROLS}/${FRAMEWORKS}/${parts[0]}/${GROUPS}/${parts[1]}/${CONTROLS}/${parts[2]}`;
        } else {
            throw new Error(getFrameworkGroupControlURLUnparsableIdentifier);
        }
    } else {
        // The given identifier is a Control Framework.
        return `/${OPERATIONAL_CONTROLS}/${FRAMEWORKS}#${identifier}`;
    }
};

/**
 * Given a Notification, determine the URL (route) for the entity (Action, Control, Exception, etc.) that it is related to.
 */
export const getNotificationUrl = (notification: UserNotification): string => {
    switch (notification._type) {
        case NotificationType.ACTION_DUE:
            return generatePath(ACTION_DETAILS, { action_id: notification.identifier });
        case NotificationType.CONTROL_ASSESSMENT:
            return getFrameworkGroupControlURL(notification.control.identifier);
        case NotificationType.EXCEPTION_EXPIRY:
            return getExceptionDetailsUrl(notification.identifier, notification.exception.type);
        case NotificationType.ISSUE_DUE:
            return getIssueDetailsUrl(notification.identifier, notification.issue.type);

        /** UCRM */
        case NotificationType.UCRM_DUE_DILIGENCE_WORKFLOW_DUE:
            return generatePath(UCRM_USE_CASE_DASHBOARD, { third_party_id: notification.use_case.vendor_id, service_id: notification.use_case.id });
        case NotificationType.UCRM_FOLDER_EXPIRY:
            return generatePath(UCRM_FOLDER_DETAILS, { third_party_id: notification.business.id, folder_id: notification.folder.id });
        case NotificationType.UCRM_IRQ_DUE:
            return generatePath(UCRM_INHERENT_RISK_QUESTIONNAIRE, { third_party_id: notification.use_case.vendor_id, service_id: notification.use_case.id });
        case NotificationType.UCRM_DUE_DILIGENCE_ASSESSMENT_DUE:
            return generatePath(UCRM_ASSESSMENT, { third_party_id: notification.assessment.third_party_id, service_id: notification.assessment.service_id, assessment_id: notification.assessment.id });

        /** TPRM */
        case NotificationType.TPRM_DUE_DILIGENCE_WORKFLOW_DUE:
            const [thirdPartyId, serviceId] = notification.identifier.split('#');
            return generatePath(TPRM_SERVICE_DASHBOARD, { third_party_id: thirdPartyId, service_id: serviceId });
        case NotificationType.TPRM_FOLDER_EXPIRY:
            return generatePath(TPRM_FOLDER_DETAILS, { third_party_id: notification.third_party.id, folder_id: notification.folder.id });
        case NotificationType.TPRM_IRQ_DUE:
            return generatePath(TPRM_INHERENT_RISK_QUESTIONNAIRE, { third_party_id: notification.third_party_service.vendor_id, service_id: notification.third_party_service.id });
        case NotificationType.TPRM_DUE_DILIGENCE_ASSESSMENT_DUE:
            return generatePath(TPRM_ASSESSMENT, { third_party_id: notification.assessment.third_party_id, service_id: notification.assessment.service_id, assessment_id: notification.assessment.id });
    }
};

/**
 * Returns a link to the Manage Issue page, where the user can create a new issue.
 * Used by the `IssuesListing` component, which provides parameters supplied by its parent component that indicate the page where a click on "Create Issue" originates.
 * If the ID of a control or a third party is provided, the page will preselect the corresponding entity.
 */
export const getCreateIssueUrl = (issueType: ProductModule, origin: ManageIssueOrigin, preselectedEntityId?: string): string => {
    const pathPrefix = pathPrefixForIssuesExceptionsModule(issueType);
    const params = new URLSearchParams();
    if (preselectedEntityId) {
        params.set('impactedEntityId', preselectedEntityId);
    }
    appendManageIssueOriginSearchParams(params, origin);
    const queryString = params.toString();
    return `${pathPrefix}/${ISSUES}/${CREATE}${queryString ? `?${queryString}` : ''}`;
};

/** Encodes a `ManageIssueOrigin` into the provided search params. Symmetric with `parseManageIssueOrigin`. */
const appendManageIssueOriginSearchParams = (params: URLSearchParams, origin: ManageIssueOrigin): void => {
    params.set(SEARCH_PARAM_ISSUE_ORIGIN_SOURCE, origin.source);
    switch (origin.source) {
        case IssueBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
        case IssueBreadcrumbSource.FINAL_REVIEW:
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ORGANIZATION_ID, origin.organizationId);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_ID, origin.assessmentSubjectId);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_NAME, origin.assessmentSubjectDisplayName);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_MODULE, origin.module);
            return;
        case IssueBreadcrumbSource.ASSESSMENT:
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ORGANIZATION_ID, origin.organizationId);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_ID, origin.assessmentSubjectId);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_NAME, origin.assessmentSubjectDisplayName);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_ID, origin.assessmentId);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_NAME, origin.assessmentName);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_MODULE, origin.module);
            return;
        case IssueBreadcrumbSource.CONTROL_DETAILS:
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_IDENTIFIER, origin.controlIdentifier);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_GROUP_NAME, origin.controlGroupName);
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_NAME, origin.controlDisplayName);
            return;
        case IssueBreadcrumbSource.ISSUE_DETAILS:
        case IssueBreadcrumbSource.ISSUES_LISTING:
            params.set(SEARCH_PARAM_ISSUE_ORIGIN_MODULE, origin.module);
            return;
        default: {
            // Compile-time exhaustiveness check: if a new `IssueBreadcrumbSource` is added without a case above, `origin` is no longer `never` and this assignment fails to compile.
            const exhaustiveCheck: never = origin;
            return exhaustiveCheck;
        }
    }
};

/**
 * Decodes a `ManageIssueOrigin` from search params produced by `getCreateIssueUrl`.
 * Throws an exception if the source is unrecognized or any expected search params are missing.
 */
export const parseManageIssueOrigin = (searchParams: URLSearchParams): ManageIssueOrigin => {
    const getRequiredOriginParam = (searchParams: URLSearchParams, key: string): string => {
        const value = searchParams.get(key);
        if (value === null) {
            throw new Error(`Missing required issue breadcrumb search param: ${key}`);
        }
        return value;
    };

    const source = searchParams.get(SEARCH_PARAM_ISSUE_ORIGIN_SOURCE);
    switch (source) {
        case IssueBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
        case IssueBreadcrumbSource.FINAL_REVIEW: {
            const organizationId = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ORGANIZATION_ID);
            const assessmentSubjectId = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_ID);
            const assessmentSubjectDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_NAME);
            const module = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_MODULE) as DueDiligenceModule;
            return { source, organizationId, assessmentSubjectId, assessmentSubjectDisplayName, module };
        }
        case IssueBreadcrumbSource.ASSESSMENT: {
            const organizationId = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ORGANIZATION_ID);
            const assessmentSubjectId = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_ID);
            const assessmentSubjectDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_SUBJECT_NAME);
            const assessmentId = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_ID);
            const assessmentName = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_ASSESSMENT_NAME);
            const module = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_MODULE) as DueDiligenceModule;
            return { source, organizationId, assessmentSubjectId, assessmentSubjectDisplayName, assessmentId, assessmentName, module };
        }
        case IssueBreadcrumbSource.CONTROL_DETAILS: {
            const controlIdentifier = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_IDENTIFIER);
            const controlGroupName = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_GROUP_NAME);
            const controlDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_CONTROL_NAME);
            return { source, controlIdentifier, controlGroupName, controlDisplayName };
        }
        case IssueBreadcrumbSource.ISSUE_DETAILS:
        case IssueBreadcrumbSource.ISSUES_LISTING: {
            const module = getRequiredOriginParam(searchParams, SEARCH_PARAM_ISSUE_ORIGIN_MODULE) as ProductModule;
            return { source, module };
        }
        default:
            throw new Error(`Unrecognized or missing issue breadcrumb source: ${source}`);
    }
};

/** Builds a breadcrumb trail that mirrors the page a "Create Issue" action originated from. */
export const getManageIssueOriginBreadcrumbSegments = (origin: ManageIssueOrigin): BreadcrumbSegment[] => {
    switch (origin.source) {
        case IssueBreadcrumbSource.ASSESSMENT:
            return [{ label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) }, { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) }, { label: 'Assessments' }, { label: origin.assessmentName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_ASSESSMENT : TPRM_ASSESSMENT, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId, assessment_id: origin.assessmentId }) }];
        case IssueBreadcrumbSource.CONTROL_DETAILS: {
            const [controlFramework, controlGroupId] = origin.controlIdentifier.split('#');
            return [
                { label: controlFramework, link: getFrameworkGroupControlURL(controlFramework) },
                { label: origin.controlGroupName, link: getFrameworkGroupControlURL(`${controlFramework}#${controlGroupId}`) },
                { label: origin.controlDisplayName, link: `${getFrameworkGroupControlURL(origin.controlIdentifier)}#issues` },
            ];
        }
        case IssueBreadcrumbSource.FINAL_REVIEW:
            return [
                { label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) },
                { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
                { label: 'Final Review', link: generatePath(origin.module === ProductModule.UCRM ? UCRM_FINAL_REVIEW : TPRM_FINAL_REVIEW, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
            ];
        case IssueBreadcrumbSource.ISSUE_DETAILS:
        case IssueBreadcrumbSource.ISSUES_LISTING: {
            const moduleListingPath = issuesExceptionsListingPathForModule(origin.module);
            return [{ label: origin.module, link: `${moduleListingPath}#issues` }];
        }
        case IssueBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
            return [
                { label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) },
                { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
            ];
    }
};

/**
 * Returns a link to the Issue Details page.
 */
export const getIssueDetailsUrl = (issueId: string, issueType: ProductModule): string => {
    const pathPrefix = pathPrefixForIssuesExceptionsModule(issueType);
    return `${pathPrefix}/${ISSUES}/${issueId}`;
};

/**
 * Returns a link to the Manage Issue page, where the user can save changes to an existing issue. If `isClosingIssue` is true, the page will include form fields specific to closing the issue.
 * Editing is always launched from the Issue Details page, so an `ISSUE_DETAILS` origin is encoded to drive the page's breadcrumb.
 */
export const getUpdateIssueUrl = (issueId: string, issueType: ProductModule, isClosingIssue: boolean): string => {
    const pathPrefix = pathPrefixForIssuesExceptionsModule(issueType);
    const params = new URLSearchParams();
    params.set('closing', String(isClosingIssue));
    appendManageIssueOriginSearchParams(params, { source: IssueBreadcrumbSource.ISSUE_DETAILS, module: issueType });
    return `${pathPrefix}/${ISSUES}/${issueId}/${CONFIGURATION}?${params.toString()}`;
};

/**
 * Returns a link to the Manage Exception page, where the user can create a new exception.
 * Used by the `ExceptionsListing` component, which provides parameters supplied by its parent component that indicate the page where a click on "Create Exception" originates.
 * If the ID of a control or a third party is provided, the page will preselect the corresponding entity.
 */
export const getCreateExceptionUrl = (exceptionType: ProductModule, origin: ManageExceptionOrigin, preselectedEntityId?: string): string => {
    const pathPrefix = pathPrefixForIssuesExceptionsModule(exceptionType);
    const params = new URLSearchParams();
    if (preselectedEntityId) {
        params.set('impactedEntityId', preselectedEntityId);
    }
    appendManageExceptionOriginSearchParams(params, origin);
    const queryString = params.toString();
    return `${pathPrefix}/${EXCEPTIONS}/${CREATE}${queryString ? `?${queryString}` : ''}`;
};

/** Encodes a `ManageExceptionOrigin` into the provided search params. Symmetric with `parseManageExceptionOrigin`. */
const appendManageExceptionOriginSearchParams = (params: URLSearchParams, origin: ManageExceptionOrigin): void => {
    params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_SOURCE, origin.source);
    switch (origin.source) {
        case ExceptionBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
        case ExceptionBreadcrumbSource.FINAL_REVIEW:
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ORGANIZATION_ID, origin.organizationId);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_ID, origin.assessmentSubjectId);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_NAME, origin.assessmentSubjectDisplayName);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE, origin.module);
            return;
        case ExceptionBreadcrumbSource.ASSESSMENT:
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ORGANIZATION_ID, origin.organizationId);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_ID, origin.assessmentSubjectId);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_NAME, origin.assessmentSubjectDisplayName);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_ID, origin.assessmentId);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_NAME, origin.assessmentName);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE, origin.module);
            return;
        case ExceptionBreadcrumbSource.CONTROL_DETAILS:
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_IDENTIFIER, origin.controlIdentifier);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_GROUP_NAME, origin.controlGroupName);
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_NAME, origin.controlDisplayName);
            return;
        case ExceptionBreadcrumbSource.EXCEPTION_DETAILS:
        case ExceptionBreadcrumbSource.EXCEPTIONS_LISTING:
            params.set(SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE, origin.module);
            return;
        default: {
            // Compile-time exhaustiveness check: if a new `ExceptionBreadcrumbSource` is added without a case above, `origin` is no longer `never` and this assignment fails to compile.
            const exhaustiveCheck: never = origin;
            return exhaustiveCheck;
        }
    }
};

/**
 * Decodes a `ManageExceptionOrigin` from search params produced by `getCreateExceptionUrl`.
 * Throws an exception if the source is unrecognized or any expected search params are missing.
 */
export const parseManageExceptionOrigin = (searchParams: URLSearchParams): ManageExceptionOrigin => {
    const getRequiredOriginParam = (searchParams: URLSearchParams, key: string): string => {
        const value = searchParams.get(key);
        if (value === null) {
            throw new Error(`Missing required exception breadcrumb search param: ${key}`);
        }
        return value;
    };

    const source = searchParams.get(SEARCH_PARAM_EXCEPTION_ORIGIN_SOURCE);
    switch (source) {
        case ExceptionBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
        case ExceptionBreadcrumbSource.FINAL_REVIEW: {
            const organizationId = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ORGANIZATION_ID);
            const assessmentSubjectId = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_ID);
            const assessmentSubjectDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_NAME);
            const module = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE) as DueDiligenceModule;
            return { source, organizationId, assessmentSubjectId, assessmentSubjectDisplayName, module };
        }
        case ExceptionBreadcrumbSource.ASSESSMENT: {
            const organizationId = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ORGANIZATION_ID);
            const assessmentSubjectId = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_ID);
            const assessmentSubjectDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_SUBJECT_NAME);
            const assessmentId = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_ID);
            const assessmentName = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_ASSESSMENT_NAME);
            const module = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE) as DueDiligenceModule;
            return { source, organizationId, assessmentSubjectId, assessmentSubjectDisplayName, assessmentId, assessmentName, module };
        }
        case ExceptionBreadcrumbSource.CONTROL_DETAILS: {
            const controlIdentifier = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_IDENTIFIER);
            const controlGroupName = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_GROUP_NAME);
            const controlDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_CONTROL_NAME);
            return { source, controlIdentifier, controlGroupName, controlDisplayName };
        }
        case ExceptionBreadcrumbSource.EXCEPTION_DETAILS:
        case ExceptionBreadcrumbSource.EXCEPTIONS_LISTING: {
            const module = getRequiredOriginParam(searchParams, SEARCH_PARAM_EXCEPTION_ORIGIN_MODULE) as ProductModule;
            return { source, module };
        }
        default:
            throw new Error(`Unrecognized or missing exception breadcrumb source: ${source}`);
    }
};

/** Builds a breadcrumb trail that mirrors the page a "Create Exception" action originated from. */
export const getManageExceptionOriginBreadcrumbSegments = (origin: ManageExceptionOrigin): BreadcrumbSegment[] => {
    switch (origin.source) {
        case ExceptionBreadcrumbSource.ASSESSMENT:
            return [{ label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) }, { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) }, { label: 'Assessments' }, { label: origin.assessmentName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_ASSESSMENT : TPRM_ASSESSMENT, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId, assessment_id: origin.assessmentId }) }];
        case ExceptionBreadcrumbSource.CONTROL_DETAILS: {
            const [controlFramework, controlGroupId] = origin.controlIdentifier.split('#');
            return [
                { label: controlFramework, link: getFrameworkGroupControlURL(controlFramework) },
                { label: origin.controlGroupName, link: getFrameworkGroupControlURL(`${controlFramework}#${controlGroupId}`) },
                { label: origin.controlDisplayName, link: `${getFrameworkGroupControlURL(origin.controlIdentifier)}#exceptions` },
            ];
        }
        case ExceptionBreadcrumbSource.FINAL_REVIEW:
            return [
                { label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) },
                { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
                { label: 'Final Review', link: generatePath(origin.module === ProductModule.UCRM ? UCRM_FINAL_REVIEW : TPRM_FINAL_REVIEW, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
            ];
        case ExceptionBreadcrumbSource.EXCEPTION_DETAILS:
        case ExceptionBreadcrumbSource.EXCEPTIONS_LISTING: {
            // Append the "exceptions" hash so the link lands on the Exceptions tab (rather than the page's first tab) of the Issues & Exceptions page.
            const moduleListingPath = issuesExceptionsListingPathForModule(origin.module);
            return [{ label: origin.module, link: `${moduleListingPath}#exceptions` }];
        }
        case ExceptionBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
            return [
                { label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) },
                { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
            ];
    }
};

/**
 * Returns a link to the Exception Details page.
 */
export const getExceptionDetailsUrl = (exceptionId: string, exceptionType: ProductModule): string => {
    const pathPrefix = pathPrefixForIssuesExceptionsModule(exceptionType);
    return `${pathPrefix}/${EXCEPTIONS}/${exceptionId}`;
};

/**
 * Returns a link to the Manage Exception page, where the user can save changes to an existing exception. If `isClosingException` is true, the page will include form fields specific to closing the exception.
 * Editing is always launched from the Exception Details page, so an `EXCEPTION_DETAILS` origin is encoded to drive the page's breadcrumb.
 */
export const getUpdateExceptionUrl = (exceptionId: string, exceptionType: ProductModule, isClosingException: boolean): string => {
    const pathPrefix = pathPrefixForIssuesExceptionsModule(exceptionType);
    const params = new URLSearchParams();
    params.set('closing', String(isClosingException));
    appendManageExceptionOriginSearchParams(params, { source: ExceptionBreadcrumbSource.EXCEPTION_DETAILS, module: exceptionType });
    return `${pathPrefix}/${EXCEPTIONS}/${exceptionId}/${CONFIGURATION}?${params.toString()}`;
};

/**
 * Returns a link to the Create Folder page.
 * Used by the `FoldersListing` component, which provides parameters supplied by its parent component that indicate the page where a click on "Create Folder" originates.
 * If the ID of a third party is provided, the page will preselect that third party and prevent the user from changing it.
 */
export const getCreateFolderUrl = (origin: ManageFolderOrigin, preselectedOrganizationId?: string): string => {
    const params = new URLSearchParams();
    if (preselectedOrganizationId) {
        params.set(SEARCH_PARAM_FOLDER_PRESELECTED_ORGANIZATION_ID, preselectedOrganizationId);
    }
    appendManageFolderOriginSearchParams(params, origin);
    return `${generatePath(origin.module === ProductModule.UCRM ? UCRM_ADD_NEW_FOLDER : TPRM_ADD_NEW_FOLDER)}?${params.toString()}`;
};

/** Returns a link to the Folder Details page, carrying the origin so that the page (and the pages reachable from it) can render an origin-aware breadcrumb. */
export const getFolderDetailsUrl = (organizationId: string, folderId: string, origin: ManageFolderOrigin): string => {
    return appendManageFolderOriginToPath(generatePath(origin.module === ProductModule.UCRM ? UCRM_FOLDER_DETAILS : TPRM_FOLDER_DETAILS, { third_party_id: organizationId, folder_id: folderId }), origin);
};

/** Returns a link to the Manage Folder page, where the user can edit the current version of an existing folder. */
export const getManageFolderUrl = (organizationId: string, folderId: string, origin: ManageFolderOrigin): string => {
    return appendManageFolderOriginToPath(generatePath(origin.module === ProductModule.UCRM ? UCRM_MANAGE_FOLDER : TPRM_MANAGE_FOLDER, { third_party_id: organizationId, folder_id: folderId }), origin);
};

/** Returns a link to the Create Folder Version page. */
export const getAddFolderVersionUrl = (organizationId: string, folderId: string, origin: ManageFolderOrigin): string => {
    return appendManageFolderOriginToPath(generatePath(origin.module === ProductModule.UCRM ? UCRM_ADD_FOLDER_VERSION : TPRM_ADD_FOLDER_VERSION, { third_party_id: organizationId, folder_id: folderId }), origin);
};

/** Appends an encoded `ManageFolderOrigin` to a folder page path as a query string. */
const appendManageFolderOriginToPath = (path: string, origin: ManageFolderOrigin): string => {
    const params = new URLSearchParams();
    appendManageFolderOriginSearchParams(params, origin);
    return `${path}?${params.toString()}`;
};

/** Encodes a `ManageFolderOrigin` into the provided search params. Symmetric with `parseManageFolderOrigin`. */
const appendManageFolderOriginSearchParams = (params: URLSearchParams, origin: ManageFolderOrigin): void => {
    params.set(SEARCH_PARAM_FOLDER_ORIGIN_SOURCE, origin.source);
    params.set(SEARCH_PARAM_FOLDER_ORIGIN_MODULE, origin.module);
    switch (origin.source) {
        case FolderBreadcrumbSource.FOLDERS_LISTING:
            return;
        case FolderBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
            params.set(SEARCH_PARAM_FOLDER_ORIGIN_ORGANIZATION_ID, origin.organizationId);
            params.set(SEARCH_PARAM_FOLDER_ORIGIN_ASSESSMENT_SUBJECT_ID, origin.assessmentSubjectId);
            params.set(SEARCH_PARAM_FOLDER_ORIGIN_ASSESSMENT_SUBJECT_NAME, origin.assessmentSubjectDisplayName);
            return;
        default: {
            // Compile-time exhaustiveness check: if a new `FolderBreadcrumbSource` is added without a case above, `origin` is no longer `never` and this assignment fails to compile.
            const exhaustiveCheck: never = origin;
            return exhaustiveCheck;
        }
    }
};

/**
 * Decodes a `ManageFolderOrigin` from search params produced by `getCreateFolderUrl` and friends.
 * `fallbackModule` is used when the search params carry no recognizable origin (e.g., the page was navigated to directly rather than via one of the URL builders above).
 */
export const parseManageFolderOrigin = (searchParams: URLSearchParams, fallbackModule: DueDiligenceModule = ProductModule.TPRM): ManageFolderOrigin => {
    const getRequiredOriginParam = (searchParams: URLSearchParams, key: string): string => {
        const value = searchParams.get(key);
        if (value === null) {
            throw new Error(`Missing required folder breadcrumb search param: ${key}`);
        }
        return value;
    };

    const source = searchParams.get(SEARCH_PARAM_FOLDER_ORIGIN_SOURCE);
    const module = (searchParams.get(SEARCH_PARAM_FOLDER_ORIGIN_MODULE) as DueDiligenceModule | null) ?? fallbackModule;
    switch (source) {
        case FolderBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD: {
            const organizationId = getRequiredOriginParam(searchParams, SEARCH_PARAM_FOLDER_ORIGIN_ORGANIZATION_ID);
            const assessmentSubjectId = getRequiredOriginParam(searchParams, SEARCH_PARAM_FOLDER_ORIGIN_ASSESSMENT_SUBJECT_ID);
            const assessmentSubjectDisplayName = getRequiredOriginParam(searchParams, SEARCH_PARAM_FOLDER_ORIGIN_ASSESSMENT_SUBJECT_NAME);
            return { source, module, organizationId, assessmentSubjectId, assessmentSubjectDisplayName };
        }
        case FolderBreadcrumbSource.FOLDERS_LISTING:
        default:
            return { source: FolderBreadcrumbSource.FOLDERS_LISTING, module };
    }
};

/** Builds a breadcrumb trail that mirrors the page a folder was navigated to from. */
export const getManageFolderOriginBreadcrumbSegments = (origin: ManageFolderOrigin): BreadcrumbSegment[] => {
    switch (origin.source) {
        case FolderBreadcrumbSource.FOLDERS_LISTING:
            return [{ label: origin.module === ProductModule.UCRM ? 'Business Folders' : 'Third-Party Folders', link: generatePath(origin.module === ProductModule.UCRM ? UCRM_FOLDERS_LISTING : TPRM_FOLDERS_LISTING) }];
        case FolderBreadcrumbSource.ASSESSMENT_SUBJECT_DASHBOARD:
            return [
                { label: origin.module, link: organizationsAndAssessmentSubjectsListingPathForModule(origin.module) },
                { label: origin.assessmentSubjectDisplayName, link: generatePath(origin.module === ProductModule.UCRM ? UCRM_USE_CASE_DASHBOARD : TPRM_SERVICE_DASHBOARD, { third_party_id: origin.organizationId, service_id: origin.assessmentSubjectId }) },
            ];
    }
};
